import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, Plus, Search, Rss, ChevronRight,
  Loader2, AlertCircle, CheckCircle, XCircle, Users, X, Zap, Image
} from 'lucide-react';
import { toast } from 'sonner';
import api, { automationAPI } from '../lib/api';


const HEALTH_BADGES = {
  healthy:  { colour: 'bg-emerald-50 text-emerald-700 border-emerald-200',  icon: CheckCircle },
  warning:  { colour: 'bg-amber-50 text-amber-700 border-amber-200',        icon: AlertCircle },
  degraded: { colour: 'bg-orange-50 text-orange-700 border-orange-200',     icon: AlertCircle },
  failed:   { colour: 'bg-red-50 text-red-600 border-red-200',              icon: XCircle },
  blocked:  { colour: 'bg-rose-50 text-rose-600 border-rose-200',           icon: XCircle },
  unknown:  { colour: 'bg-slate-100 text-slate-500 border-slate-300',       icon: AlertCircle },
};

const PATCH_LABELS = {
  autopilot_enabled:            'Fetch autopilot',
  generation_autopilot_enabled: 'Generation autopilot',
  use_source_images:            'Source images',
};

const fmtUK = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    timeZone: 'Europe/London',
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
};

const PARSER_TYPES = [
  { value: '',                 label: 'All types' },
  { value: 'rss',              label: 'RSS' },
  { value: 'atom',             label: 'Atom' },
  { value: 'sitemap_news',     label: 'News sitemap' },
  { value: 'newsroom_listing', label: 'Newsroom listing' },
  { value: 'jsonld_news',      label: 'JSON-LD' },
  { value: 'custom',           label: 'Custom' },
];

const HEALTH_OPTS = [
  { value: '',         label: 'All health' },
  { value: 'healthy',  label: 'Healthy' },
  { value: 'warning',  label: 'Warning' },
  { value: 'degraded', label: 'Degraded' },
  { value: 'failed',   label: 'Failed' },
  { value: 'blocked',  label: 'Blocked' },
];

const ACTIVITY_OPTS = [
  { value: '',         label: 'All states' },
  { value: 'active',   label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const SUBS_OPTS = [
  { value: '',     label: 'Any subscriptions' },
  { value: 'has',  label: 'Has subscriptions' },
  { value: 'none', label: 'No subscriptions' },
];

const AUTOPILOT_OPTS = [
  { value: '',         label: 'Any autopilot' },
  { value: 'either',   label: 'Fetch or generate on' },
  { value: 'fetch',    label: 'Fetch on' },
  { value: 'generate', label: 'Generate on' },
  { value: 'off',      label: 'Both off' },
];

const SOURCE_IMAGE_OPTS = [
  { value: '',    label: 'Any source images' },
  { value: 'on',  label: 'Source images on' },
  { value: 'off', label: 'Source images off' },
];

const SORT_OPTS = [
  { value: 'created_desc',       label: 'Newest added' },
  { value: 'name_asc',           label: 'Name A-Z' },
  { value: 'last_fetched_desc',  label: 'Recently fetched' },
  { value: 'items_desc',         label: 'Most items' },
  { value: 'subscriptions_desc', label: 'Most subscriptions' },
];

const selectCls =
  'px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-blue-500';


function HealthBadge({ status }) {
  const cfg = HEALTH_BADGES[status] || HEALTH_BADGES.unknown;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold border ${cfg.colour}`}>
      <Icon size={10} />
      {status || 'unknown'}
    </span>
  );
}


function ToggleWithLabel({ label, on, disabled, onChange, describe = 'autopilot' }) {
  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={`${label} ${describe}`}
        disabled={disabled}
        onClick={onChange}
        title={`${label} ${describe} ${on ? 'on' : 'off'}`}
        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-40 ${
          on ? 'bg-blue-600' : 'bg-slate-300'
        }`}
      >
        <span
          className="inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform"
          style={{ transform: on ? 'translateX(18px)' : 'translateX(4px)' }}
        />
      </button>
      <span className={`text-xs font-medium ${on ? 'text-slate-900' : 'text-slate-400'}`}>
        {label}
      </span>
    </div>
  );
}


function SourceRow({ source, models, defaultModelLabel, onOpen, onPatch }) {
  const [saving, setSaving] = useState(null);
  const fetchOn = !!source.autopilot_enabled;
  const genOn   = !!source.generation_autopilot_enabled;
  const imgOn   = source.use_source_images !== false;

  const patch = async (field, value) => {
    setSaving(field);
    try {
      const { data } = await automationAPI.updateSource(source.id, { [field]: value });
      onPatch(source.id, data && data.id ? data : { [field]: value });
      const what = PATCH_LABELS[field] || 'Setting';
      toast.success(`${what} ${value ? 'on' : 'off'} for ${source.source_name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className={`w-full border rounded-xl p-4 transition-all shadow-sm ${
      fetchOn || genOn
        ? 'bg-blue-50/40 border-blue-200 hover:border-blue-400'
        : 'bg-white border-slate-200 hover:border-blue-400'
    }`}>
      <div className="flex items-center justify-between gap-4">
        <button onClick={onOpen} className="flex-1 min-w-0 text-left group">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Rss size={14} className="text-slate-400 shrink-0" />
            <h3 className="text-slate-900 font-semibold truncate group-hover:text-blue-600 transition-colors">
              {source.source_name}
            </h3>
            <HealthBadge status={source.health_status} />
            {!source.is_active && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded border border-slate-200">
                Inactive
              </span>
            )}
            <span className="text-[10px] uppercase tracking-wider text-slate-400">{source.parser_type}</span>
          </div>

          <p className="text-xs text-slate-400 truncate font-mono ml-6">{source.source_url}</p>

          <div className="flex items-center gap-4 mt-2 ml-6 text-xs text-slate-400 flex-wrap">
            {source.primary_organisation_name && (
              <span className="inline-flex items-center gap-1">
                <Users size={11} /> {source.primary_organisation_name}
              </span>
            )}
            <span>{source.subscription_count || 0} subscriptions</span>
            <span>{source.item_count || 0} items</span>
            <span>Every {source.fetch_frequency_minutes} min</span>
            {source.last_fetched_at && (
              <span>Last: {fmtUK(source.last_fetched_at)}</span>
            )}
            {fetchOn && source.next_fetch_at && (
              <span className="text-blue-600">Next: {fmtUK(source.next_fetch_at)}</span>
            )}
          </div>
        </button>

        <div className="flex items-center gap-2 shrink-0">
          <div className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 ${
            imgOn ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-200'
          }`}
            title="When off, stories from this source never publish the image that came with the article"
          >
            <Image size={11} className={imgOn ? 'text-slate-500' : 'text-slate-300'} />
            <ToggleWithLabel
              label="Source images"
              describe="use"
              on={imgOn}
              disabled={saving === 'use_source_images'}
              onChange={() => patch('use_source_images', !imgOn)}
            />
          </div>

          <div className={`flex items-center gap-3 rounded-lg border px-3 py-1.5 ${
            fetchOn || genOn ? 'bg-white border-blue-200' : 'bg-slate-50 border-slate-200'
          }`}>
            <span className={`text-[10px] uppercase tracking-wider font-semibold inline-flex items-center gap-1 ${
              fetchOn || genOn ? 'text-blue-700' : 'text-slate-400'
            }`}>
              <Zap size={11} /> Autopilot
            </span>
            <span className="w-px h-5 bg-slate-200" />
            <ToggleWithLabel
              label="Fetch"
              on={fetchOn}
              disabled={saving === 'autopilot_enabled' || !source.is_active}
              onChange={() => patch('autopilot_enabled', !fetchOn)}
            />
            <ToggleWithLabel
              label="Generate"
              on={genOn}
              disabled={saving === 'generation_autopilot_enabled' || !source.is_active}
              onChange={() => patch('generation_autopilot_enabled', !genOn)}
            />
            <select
              value={source.default_model_id || ''}
              disabled={saving === 'default_model_id' || models.length === 0}
              onChange={e => patch('default_model_id', e.target.value || null)}
              title="Model used to generate stories from this source"
              className="px-2 py-1 bg-white border border-slate-200 rounded text-xs text-slate-700 focus:outline-none focus:border-blue-500 max-w-[170px]"
            >
              <option value="">
                {defaultModelLabel ? `Default (${defaultModelLabel})` : 'Network default'}
              </option>
              {models.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </div>

          {saving
            ? <Loader2 size={16} className="animate-spin text-slate-400" />
            : <ChevronRight size={18} className="text-slate-400" />}
        </div>
      </div>
    </div>
  );
}


export default function FeedsSourcesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [sources, setSources]           = useState([]);
  const [total, setTotal]               = useState(0);
  const [loading, setLoading]           = useState(true);
  const [publications, setPublications] = useState([]);
  const [models, setModels]             = useState([]);
  const [defaultModelId, setDefaultModelId] = useState(null);
  const [searchInput, setSearchInput]   = useState(() => searchParams.get('q') || '');

  const limit = 25;

  const search    = searchParams.get('q')         || '';
  const type      = searchParams.get('type')      || '';
  const health    = searchParams.get('health')    || '';
  const activity  = searchParams.get('activity')  || '';
  const subs      = searchParams.get('subs')      || '';
  const autopilot = searchParams.get('autopilot') || '';
  const pubId     = searchParams.get('pub')       || '';
  const srcImg    = searchParams.get('srcimg')    || '';
  const sort      = searchParams.get('sort')      || 'created_desc';
  const page      = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const offset    = (page - 1) * limit;

  useEffect(() => { loadPublications(); loadModels(); }, []);
  useEffect(() => {
    load();
  }, [search, type, health, activity, subs, autopilot, pubId, srcImg, sort, page]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== (searchParams.get('q') || '')) {
        updateParam('q', searchInput, { replace: true });
      }
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { sort, limit, offset };
      if (search)                  params.search = search;
      if (type)                    params.parser_type = type;
      if (health)                  params.health = health;
      if (activity === 'active')   params.is_active = true;
      if (activity === 'inactive') params.is_active = false;
      if (subs)                    params.has_subscriptions = subs === 'has';
      if (autopilot)               params.autopilot = autopilot;
      if (pubId)                   params.publication_id = pubId;
      if (srcImg)                  params.source_images = srcImg;
      const { data } = await api.get('/feeds/sources', { params });
      setSources(data.sources || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load sources');
    } finally {
      setLoading(false);
    }
  };

  const loadPublications = async () => {
    try {
      const { data } = await api.get('/feeds/overview');
      setPublications((data.publications || []).map(p => ({ id: p.id, name: p.name })));
    } catch {
    }
  };
  const patchSource = (id, changes) => {
    setSources(prev => prev.map(s => (s.id === id ? { ...s, ...changes } : s)));
  };

  const loadModels = async () => {
    try {
      const { data } = await api.get('/feeds/models');
      setModels(data.models || []);
      setDefaultModelId(data.default_id || null);
    } catch {
    }
  };

  const updateParam = (key, value, opts = {}) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key !== 'page') next.delete('page');
      return next;
    }, opts);
  };

  const goToPage = (nextPage) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (nextPage <= 1) next.delete('page');
      else next.set('page', String(nextPage));
      return next;
    });
  };

  const anyFilter = search || type || health || activity || subs || autopilot || pubId || srcImg;

  const clearFilters = () => {
    setSearchInput('');
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      ['q', 'type', 'health', 'activity', 'subs', 'autopilot', 'pub', 'srcimg', 'page'].forEach(k => next.delete(k));
      return next;
    });
  };

  if (user?.role !== 'admin' && user?.role !== 'editor') {
    return <div className="text-center py-16"><p className="text-slate-400">You do not have access to EventFlow.</p></div>;
  }

  const defaultModelLabel = models.find(m => m.id === defaultModelId)?.label;

  return (
    <div>
      <button
        onClick={() => navigate('/feeds')}
        className="flex items-center gap-2 text-slate-400 hover:text-slate-700 text-sm mb-4 transition-colors"
      >
        <ArrowLeft size={14} /> Back to EventFlow
      </button>

      <div className="mb-6 flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Feed Sources</h1>
          <p className="text-slate-500 text-sm mt-1">
            Network-level source registry. Each source is fetched once and fanned out to subscribing publications.
          </p>
        </div>
        <button
          onClick={() => navigate('/feeds/sources/new')}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          <Plus size={15} /> Add Source
        </button>
      </div>

      <div className="space-y-3 mb-4">
        <div className="relative max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder="Search by name, URL, or organisation..."
            className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500"
          />
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <select value={pubId} onChange={e => updateParam('pub', e.target.value)} className={selectCls}>
            <option value="">All publications</option>
            {publications.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>

          <select value={autopilot} onChange={e => updateParam('autopilot', e.target.value)} className={selectCls}>
            {AUTOPILOT_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>

          <select value={srcImg} onChange={e => updateParam('srcimg', e.target.value)} className={selectCls}>
            {SOURCE_IMAGE_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>

          <select value={type} onChange={e => updateParam('type', e.target.value)} className={selectCls}>
            {PARSER_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>

          <select value={health} onChange={e => updateParam('health', e.target.value)} className={selectCls}>
            {HEALTH_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>

          <select value={activity} onChange={e => updateParam('activity', e.target.value)} className={selectCls}>
            {ACTIVITY_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>

          <select value={subs} onChange={e => updateParam('subs', e.target.value)} className={selectCls}>
            {SUBS_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>

          <div className="ml-auto flex items-center gap-2">
            {anyFilter && (
              <button
                onClick={clearFilters}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors"
              >
                <X size={12} /> Clear
              </button>
            )}
            <select value={sort} onChange={e => updateParam('sort', e.target.value)} className={selectCls}>
              {SORT_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 gap-2 text-slate-400">
          <Loader2 size={16} className="animate-spin" /> Loading sources...
        </div>
      ) : sources.length === 0 ? (
        <div className="text-center py-16 text-slate-400 text-sm">
          {anyFilter ? 'No sources match your filters.' : 'No sources yet. Add one to get started.'}
        </div>
      ) : (
        <>
          <p className="text-xs text-slate-400 mb-3">
            Showing {offset + 1}–{Math.min(offset + sources.length, total)} of {total} · times in UK
          </p>
          <div className="space-y-2">
            {sources.map(s => (
              <SourceRow
                key={s.id}
                source={s}
                models={models}
                defaultModelLabel={defaultModelLabel}
                onOpen={() => navigate(`/feeds/sources/${s.id}`)}
                onPatch={patchSource}
              />
            ))}
          </div>
          {total > limit && (
            <div className="flex items-center justify-center gap-3 mt-5">
              <button
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1}
                className="px-3 py-1.5 bg-white hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed text-slate-600 text-sm rounded border border-slate-200"
              >
                Previous
              </button>
              <span className="text-xs text-slate-400">
                Page {page} of {Math.ceil(total / limit)}
              </span>
              <button
                onClick={() => goToPage(page + 1)}
                disabled={page * limit >= total}
                className="px-3 py-1.5 bg-white hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed text-slate-600 text-sm rounded border border-slate-200"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}